import { ConsentAction, NECESSARY } from "./types";

/** The visitor's decision, kept in a first-party cookie on the customer's site. */
export interface StoredConsent {
  /** Visitor id; the same id is sent to the Consent API so the site owner can look the visitor up. */
  id: string;
  /** Banner version the visitor answered. */
  v: number;
  /** Accepted categories (always includes "necessary"). */
  cats: string[];
  /** Every category the banner had when the visitor answered; a new category means asking again. */
  all: string[];
  act: ConsentAction;
  /** Decision time, milliseconds since the epoch. */
  ts: number;
}

export const CONSENT_COOKIE = "BerryCookieConsent";

export function readConsent(doc: Document): StoredConsent | null {
  const prefix = `${CONSENT_COOKIE}=`;
  const raw = doc.cookie
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!raw) {
    return null;
  }

  try {
    const value = JSON.parse(
      decodeURIComponent(raw.slice(prefix.length)),
    ) as Partial<StoredConsent>;
    const valid =
      typeof value.id === "string" &&
      typeof value.v === "number" &&
      Array.isArray(value.cats) &&
      Array.isArray(value.all) &&
      typeof value.act === "string" &&
      typeof value.ts === "number";
    return valid ? (value as StoredConsent) : null;
  } catch {
    return null;
  }
}

export function writeConsent(
  doc: Document,
  consent: StoredConsent,
  lifetimeMonths: number,
): void {
  const maxAge = Math.round(lifetimeMonths * 30.44 * 24 * 3600);
  const secure = doc.location?.protocol === "https:" ? "; Secure" : "";
  doc.cookie = `${CONSENT_COOKIE}=${encodeURIComponent(JSON.stringify(consent))}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

export function hasCategory(
  consent: StoredConsent | null,
  category: string,
): boolean {
  return category === NECESSARY || (consent?.cats.indexOf(category) ?? -1) >= 0;
}

/** Random v4 UUID; crypto.randomUUID only exists on secure pages. */
export function newVisitorId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") {
    return c.randomUUID();
  }
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join(
    "",
  );
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
